import React, { useState } from 'react';
import { ChevronDownIcon, MenuIcon, SearchIcon, ShoppingBasketIcon, XIcon } from 'lucide-react';
import { Link, NavLink } from 'react-router-dom';
import { Locale, useLocale } from './LocaleProvider';
type SiteLayoutProps = {
  children: React.ReactNode;
};
type LocalText = Record<Locale, string>;
const departmentGroups: {
  title: LocalText;
  links: [LocalText, string][];
}[] = [{
  title: {
    fr: 'Frais & alimentation',
    en: 'Fresh & food'
  },
  links: [[{
    fr: 'Fruits & légumes frais',
    en: 'Fresh fruit & vegetables'
  }, 'fresh-fruits-vegetables'], [{
    fr: 'Épicerie',
    en: 'Food & pantry'
  }, 'food-and-pantry'], [{
    fr: 'Boucherie, volaille & poissonnerie',
    en: 'Meat, poultry & seafood'
  }, 'meat-poultry-and-seafood'], [{
    fr: 'Boissons, vins & spiritueux',
    en: 'Beverages, wine & spirits'
  }, 'beverages-wine-and-spirits']]
}, {
  title: {
    fr: 'Maison & quotidien',
    en: 'Home & living'
  },
  links: [[{
    fr: 'Cuisine & table',
    en: 'Kitchen & dining'
  }, 'kitchen-and-dining'], [{
    fr: 'Linge de maison',
    en: 'Bedding & home textiles'
  }, 'bedding-and-home-textiles'], [{
    fr: 'Entretien & nettoyage',
    en: 'Home care & cleaning'
  }, 'home-care-and-cleaning'], [{
    fr: 'Bricolage & quincaillerie',
    en: 'DIY & hardware'
  }, 'diy-and-hardware']]
}, {
  title: {
    fr: 'Équipement & mobilité',
    en: 'Technology & mobility'
  },
  links: [[{
    fr: 'Électroménager & appareils',
    en: 'Electricals & appliances'
  }, 'electricals-and-appliances'], [{
    fr: 'Automobile',
    en: 'Automotive'
  }, 'automotive'], [{
    fr: 'Jouets, sport & voyage',
    en: 'Toys, sports & travel'
  }, 'toys-sports-and-travel'], [{
    fr: 'Approvisionnement professionnel',
    en: 'Business & institutional supply'
  }, 'business-and-institutional-supply']]
}, {
  title: {
    fr: 'Famille & bien-être',
    en: 'Family & wellness'
  },
  links: [[{
    fr: 'Santé & parapharmacie',
    en: 'Health & medical'
  }, 'health-and-medical'], [{
    fr: 'Scolaire & librairie',
    en: 'School & library'
  }, 'school-and-library'], [{
    fr: 'Bébé & animaux',
    en: 'Baby & pet care'
  }, 'baby-and-pet-care']]
}];
const labels: Record<Locale, Record<string, string>> = {
  fr: {
    heritage: 'AU SERVICE DE NOTRE COMMUNAUTÉ DEPUIS 1969 · 57 ANS',
    home: 'Accueil',
    departments: 'Rayons',
    brands: 'Marques',
    business: 'Professionnels',
    gallery: 'Galerie',
    news: 'Actualités',
    about: 'Notre société',
    contact: 'Contact',
    browse: 'Voir tous les rayons',
    request: 'Demander des informations',
    search: 'Rechercher dans le catalogue',
    toggleNav: 'Ouvrir la navigation',
    footerDescription: 'L’expérience hypermarché complète pour les foyers, les familles et les professionnels.',
    explore: 'Découvrir',
    allDepartments: 'Tous les rayons',
    ourBrands: 'Nos marques',
    storeGallery: 'Galerie du magasin',
    newsPromotions: 'Actualités & offres',
    businessTitle: 'Professionnels',
    wholesale: 'Gros & distribution',
    corporateSupply: 'Approvisionnement entreprise',
    visit: 'Nous rendre visite ou nous contacter',
    contactCompany: 'Contacter Société Supermarches Charentais'
  },
  en: {
    heritage: 'SERVING OUR COMMUNITY SINCE 1969 · 57 YEARS',
    home: 'Home',
    departments: 'Departments',
    brands: 'Brands',
    business: 'Business',
    gallery: 'Gallery',
    news: 'News',
    about: 'About us',
    contact: 'Contact',
    browse: 'Browse all departments',
    request: 'Request information',
    search: 'Search the catalogue',
    toggleNav: 'Toggle navigation',
    footerDescription: 'A complete hypermarket experience for homes, families and professionals.',
    explore: 'Explore',
    allDepartments: 'All departments',
    ourBrands: 'Our brands',
    storeGallery: 'In-store gallery',
    newsPromotions: 'News & promotions',
    businessTitle: 'For business',
    wholesale: 'Wholesale & distribution',
    corporateSupply: 'Corporate supply',
    visit: 'Visit or contact us',
    contactCompany: 'Contact Société Supermarches Charentais'
  }
};
function LanguageToggle() {
  const {
    locale,
    setLocale
  } = useLocale();
  return <div className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-0.5 text-[11px] font-extrabold tracking-wide" aria-label={locale === 'fr' ? 'Choisir la langue' : 'Choose language'} data-id="element-20">
      {(['fr', 'en'] as Locale[]).map(option => <button key={option} type="button" onClick={() => setLocale(option)} aria-pressed={locale === option} className={`rounded px-2 py-1 transition ${locale === option ? 'bg-[#102b43] text-white' : 'text-slate-500 hover:text-[#087c83]'}`} data-id="element-21">
          {option.toUpperCase()}
        </button>)}
    </div>;
}
export function SiteLayout({
  children
}: SiteLayoutProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const {
    locale
  } = useLocale();
  const text = labels[locale];
  const navItems = [[text.home, '/'], [text.departments, '/categories'], [text.brands, '/brands'], [text.business, '/wholesale'], [text.gallery, '/gallery'], [text.news, '/news'], [text.about, '/about'], [text.contact, '/contact']];
  return <div className="min-h-screen bg-[#f7f8fa] text-[#12273b]" data-id="element-22">
      <div className="bg-[#102b43] px-5 py-2 text-center text-[10px] font-bold tracking-[0.12em] text-slate-200 sm:text-[11px]" data-id="element-23">
        {text.heritage}
      </div>
      <header className="sticky top-0 z-50 border-b border-slate-200 bg-white" data-id="element-24">
        <div className="mx-auto flex h-20 max-w-[1440px] items-center px-5 lg:px-8" data-id="element-25">
          <Link to="/" className="flex items-center gap-2.5" aria-label="Société Supermarches Charentais" data-id="element-26">
            <span className="grid h-10 w-10 place-items-center rounded-xl bg-[#087c83] text-white" data-id="element-27">
              <ShoppingBasketIcon size={21} data-id="element-28" />
            </span>
            <span className="leading-none" data-id="element-29">
              <strong className="block text-sm tracking-[-0.03em]" data-id="element-30">
                SOCIÉTÉ SUPERMARCHES
              </strong>
              <small className="text-[9px] font-bold tracking-[0.16em] text-[#087c83]" data-id="element-31">
                CHARENTAIS · CENTRE LECLERC
              </small>
            </span>
          </Link>

          <nav className="ml-12 hidden h-full items-center gap-7 text-sm font-bold lg:flex" aria-label={locale === 'fr' ? 'Navigation principale' : 'Main navigation'} data-id="element-32">
            <NavLink to="/" end className={({
            isActive
          }) => isActive ? 'text-[#087c83]' : 'hover:text-[#087c83]'} data-id="element-33">
              {text.home}
            </NavLink>
            <div className="relative h-full" data-id="element-34">
              <button onClick={() => setMegaOpen(!megaOpen)} className="flex h-full items-center gap-1 hover:text-[#087c83]" aria-expanded={megaOpen} data-id="element-35">
                {text.departments} <ChevronDownIcon size={16} data-id="element-36" />
              </button>
              {megaOpen && <div className="absolute left-[-150px] top-[68px] w-[880px] rounded-b-2xl border border-slate-200 bg-white p-7 shadow-2xl" data-id="element-37">
                  <div className="grid grid-cols-4 gap-7" data-id="element-38">
                    {departmentGroups.map(group => <div key={group.title.fr} data-id="element-39">
                        <h2 className="text-sm font-extrabold text-[#087c83]" data-id="element-40">
                          {group.title[locale]}
                        </h2>
                        <ul className="mt-3 space-y-2" data-id="element-41">
                          {group.links.map(([label, id]) => <li key={id} data-id="element-42">
                              <Link to={`/categories#${id}`} onClick={() => setMegaOpen(false)} className="text-xs leading-5 text-slate-600 hover:text-[#087c83] hover:underline" data-id="element-43">
                                {label[locale]}
                              </Link>
                            </li>)}
                        </ul>
                      </div>)}
                  </div>
                  <Link to="/categories" onClick={() => setMegaOpen(false)} className="mt-7 inline-flex items-center rounded-lg bg-[#102b43] px-4 py-3 text-xs font-bold uppercase tracking-wider text-white" data-id="element-44">
                    {text.browse}
                  </Link>
                </div>}
            </div>
            {navItems.slice(2, 6).map(([label, path]) => <NavLink key={path} to={path} className={({
            isActive
          }) => isActive ? 'text-[#087c83]' : 'hover:text-[#087c83]'} data-id="element-45">
                {label}
              </NavLink>)}
          </nav>

          <div className="ml-auto hidden items-center gap-3 lg:flex" data-id="element-46">
            <LanguageToggle data-id="element-47" />
            <Link to="/categories" aria-label={text.search} className="rounded-full p-2 text-slate-600 hover:bg-slate-100" data-id="element-48">
              <SearchIcon size={20} data-id="element-49" />
            </Link>
            <Link to="/contact" className="rounded-lg bg-[#087c83] px-4 py-3 text-xs font-bold uppercase tracking-wider text-white hover:bg-[#05666d]" data-id="element-50">
              {text.request}
            </Link>
          </div>
          <div className="ml-auto flex items-center gap-2 lg:hidden" data-id="element-51">
            <LanguageToggle data-id="element-52" />
            <button onClick={() => setMenuOpen(!menuOpen)} className="rounded-lg p-2" aria-label={text.toggleNav} data-id="element-53">
              {menuOpen ? <XIcon data-id="element-54" /> : <MenuIcon data-id="element-55" />}
            </button>
          </div>
        </div>
        {menuOpen && <nav className="border-t bg-white px-5 py-4 lg:hidden" aria-label={locale === 'fr' ? 'Navigation mobile' : 'Mobile navigation'} data-id="element-56">
            {navItems.map(([label, path]) => <Link key={path} onClick={() => setMenuOpen(false)} to={path} className="block border-b border-slate-100 py-3 font-bold text-slate-700" data-id="element-57">
                {label}
              </Link>)}
          </nav>}
      </header>
      {children}
      <footer className="bg-[#102b43] px-5 py-12 text-slate-300" data-id="element-58">
        <div className="mx-auto grid max-w-[1280px] gap-10 sm:grid-cols-2 lg:grid-cols-4" data-id="element-59">
          <div data-id="element-60">
            <strong className="text-lg text-white" data-id="element-61">
              SOCIÉTÉ SUPERMARCHES CHARENTAIS
            </strong>
            <p className="mt-3 max-w-xs text-sm leading-6" data-id="element-62">
              {text.footerDescription}
            </p>
          </div>
          <FooterLinks title={text.explore} links={[[text.allDepartments, '/categories'], [text.ourBrands, '/brands'], [text.storeGallery, '/gallery'], [text.newsPromotions, '/news']]} data-id="element-63" />
          <FooterLinks title={text.businessTitle} links={[[text.wholesale, '/wholesale'], [text.request, '/contact'], [text.corporateSupply, '/wholesale']]} data-id="element-64" />
          <div data-id="element-65">
            <h2 className="font-bold text-white" data-id="element-66">{text.visit}</h2>
            <p className="mt-3 text-sm leading-6" data-id="element-67">
              Société Supermarches Charentais
              <br data-id="element-68" />
              Centre Leclerc, Avenue du Fief Rose
              <br data-id="element-69" />
              17140 Lagord, France
              <br data-id="element-70" />
              +33 9 77 21 80 15
            </p>
            <Link to="/contact" className="mt-4 inline-block text-sm font-bold text-[#7ee1df]" data-id="element-71">
              {text.contactCompany} →
            </Link>
          </div>
        </div>
      </footer>
    </div>;
}
function FooterLinks({
  title,
  links
}: {
  title: string;
  links: [string, string][];
}) {
  return <div data-id="element-72">
      <h2 className="font-bold text-white" data-id="element-73">{title}</h2>
      <ul className="mt-3 space-y-2 text-sm" data-id="element-74">
        {links.map(([label, path]) => <li key={path} data-id="element-75">
            <Link className="hover:text-white" to={path} data-id="element-76">
              {label}
            </Link>
          </li>)}
      </ul>
    </div>;
}